import { useEffect, useMemo, useState } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import {
  App as AntdApp,
  Button,
  Card,
  DatePicker,
  Drawer,
  Form,
  Image,
  Input,
  InputNumber,
  Popconfirm,
  Segmented,
  Select,
  Space,
  Switch,
  Table,
  Tag,
  Tooltip,
  Typography,
} from 'antd'
import type { ColumnsType } from 'antd/es/table'
import {
  DeleteOutlined,
  EditOutlined,
  EyeOutlined,
  LinkOutlined,
  PlusOutlined,
  ReloadOutlined,
} from '@ant-design/icons'
import type { Dayjs } from 'dayjs'
import { supabase } from '@/lib/supabase'
import { revalidateNextTag } from '@/lib/revalidate'
import { formatCST, nowCST, pickerToUTCISO, toCSTDayjs } from '@/lib/time'
import { AdminMobileList } from '@/components/AdminMobileList'
import { AdminRecordActions } from '@/components/AdminRecordActions'
import { AdminFilterBar } from '@/components/AdminFilterBar'
import { useIsMobile } from '@/lib/hooks'

export const Route = createFileRoute('/_authed/ad-slots')({
  component: AdSlotsPage,
})

/* ── placement catalog ───────────────────────────────────────── */

type PlacementCode =
  | 'home_carousel'
  | 'home_below_carousel'
  | 'schedule_below_weekly'
  | 'desktop_topbar_schedule_after'
  | 'recent_below_list'
  | 'player_below'
  | 'player_pause'
  | 'player_pause_overlay'
  | 'profile_banner'
  | 'search_banner'

type ActiveFilter = 'active' | 'inactive' | 'all'
type Platform = 'web' | 'client'
type PlatformFilter = Platform | 'all'
type CreativeKind = 'image' | 'text_link'

interface PlacementMeta {
  label: string
  location: string
  rows: number
  columns: number
  color: string
  creative: CreativeKind
  platforms: Platform[]
}

const AD_PLACEMENTS: Record<PlacementCode, PlacementMeta> = {
  home_carousel: {
    label: '首页轮播（Client）',
    location: 'Client home carousel',
    rows: 1,
    columns: 10,
    color: 'geekblue',
    creative: 'image',
    platforms: ['client'],
  },
  home_below_carousel: {
    label: '首页轮播下方',
    location: 'Home page below carousel',
    rows: 2,
    columns: 2,
    color: 'blue',
    creative: 'image',
    platforms: ['web', 'client'],
  },
  schedule_below_weekly: {
    label: '追番表下方',
    location: 'Weekly schedule below schedule grid',
    rows: 2,
    columns: 2,
    color: 'cyan',
    creative: 'image',
    platforms: ['web', 'client'],
  },
  desktop_topbar_schedule_after: {
    label: '桌面端顶栏周表后',
    location: 'Desktop navigation three-column text links after schedule',
    rows: 1,
    columns: 3,
    color: 'magenta',
    creative: 'text_link',
    platforms: ['web'],
  },
  recent_below_list: {
    label: '近期更新列表下方',
    location: 'Recent/new anime page below list',
    rows: 2,
    columns: 2,
    color: 'green',
    creative: 'image',
    platforms: ['web', 'client'],
  },
  player_below: {
    label: '播放器下方',
    location: 'Player page below player',
    rows: 2,
    columns: 2,
    color: 'purple',
    creative: 'image',
    platforms: ['web', 'client'],
  },
  player_pause_overlay: {
    label: '播放器暂停浮层（Web）',
    location: 'Web player pause overlay',
    rows: 1,
    columns: 1,
    color: 'volcano',
    creative: 'image',
    platforms: ['web'],
  },
  player_pause: {
    label: '播放器暂停广告（Client）',
    location: 'Client player pause material',
    rows: 1,
    columns: 1,
    color: 'orange',
    creative: 'image',
    platforms: ['client'],
  },
  profile_banner: {
    label: '个人中心横幅（Client）',
    location: 'Client profile banner',
    rows: 1,
    columns: 1,
    color: 'gold',
    creative: 'image',
    platforms: ['client'],
  },
  search_banner: {
    label: '搜索页横幅（Client）',
    location: 'Client search banner',
    rows: 1,
    columns: 1,
    color: 'lime',
    creative: 'image',
    platforms: ['client'],
  },
}

const PLACEMENT_OPTIONS: Array<{ value: PlacementCode; label: string }> = (
  Object.entries(AD_PLACEMENTS) as Array<[PlacementCode, PlacementMeta]>
).map(([value, meta]) => ({ value, label: meta.label }))

const PLATFORM_OPTIONS: Array<{ value: Platform; label: string }> = [
  { value: 'web', label: 'Web 网页版' },
  { value: 'client', label: 'Client 客户端' },
]

/* ── types ───────────────────────────────────────────────────── */

interface AdSlotRow {
  id: string
  platform: Platform
  placement_code: PlacementCode | string
  row_no: number
  column_no: number
  title: string
  image_url: string
  click_url: string
  alt_text: string | null
  is_active: boolean
  starts_at: string | null
  expires_at: string | null
  sort_order: number
  created_by: string | null
  created_at: string
  updated_at: string
}

interface ListResult {
  ok: boolean
  rows?: AdSlotRow[]
  total?: number
  error?: string
}

interface FormValues {
  platform: Platform
  placement_code: PlacementCode
  row_no: number
  column_no: number
  title: string
  image_url?: string
  click_url: string
  alt_text: string | null
  is_active: boolean
  time_range: [Dayjs | null, Dayjs | null] | null
  sort_order: number
  reason: string
}

/* ── helpers ─────────────────────────────────────────────────── */

function isPlacementCode(code: string): code is PlacementCode {
  return code in AD_PLACEMENTS
}

function placementMeta(code: string): PlacementMeta {
  if (isPlacementCode(code)) return AD_PLACEMENTS[code]
  return {
    label: code,
    location: 'Unknown placement',
    rows: 99,
    columns: 99,
    color: 'default',
    creative: 'image',
    platforms: ['web', 'client'],
  }
}

function placementLabel(code: string): string {
  return placementMeta(code).label
}

function placementLayout(
  platform: Platform,
  code: string,
): Pick<PlacementMeta, 'rows' | 'columns'> {
  if (platform === 'client' && code === 'player_below') {
    return { rows: 1, columns: 1 }
  }
  const meta = placementMeta(code)
  return { rows: meta.rows, columns: meta.columns }
}

function supportsPlatform(platform: Platform, code: string): boolean {
  return placementMeta(code).platforms.includes(platform)
}

function defaultPlatformForPlacement(code: PlacementCode | 'all'): Platform {
  if (code === 'all') return 'web'
  return placementMeta(code).platforms[0] ?? 'web'
}

function defaultPlacementForPlatform(platform: Platform): PlacementCode {
  return platform === 'client' ? 'home_carousel' : 'home_below_carousel'
}

function isTextLinkPlacement(code: string): boolean {
  return placementMeta(code).creative === 'text_link'
}

function slotCoordinate(row: Pick<AdSlotRow, 'row_no' | 'column_no'>): string {
  return `第 ${row.row_no} 行 / 第 ${row.column_no} 列`
}

function getDeliveryState(row: AdSlotRow): {
  label: string
  color: string
  hint: string
} {
  if (!row.is_active) {
    return { label: '已关闭', color: 'default', hint: 'C 端不会展示' }
  }
  const now = nowCST()
  if (row.starts_at && now.isBefore(row.starts_at)) {
    return { label: '未开始', color: 'processing', hint: '到开始时间后展示' }
  }
  if (row.expires_at && !now.isBefore(row.expires_at)) {
    return { label: '已过期', color: 'warning', hint: '已超过过期时间' }
  }
  return { label: '投放中', color: 'success', hint: '当前满足公开读取条件' }
}

function mapErrorMessage(message: string): string {
  const msgMap: Record<string, string> = {
    forbidden: '无权限',
    invalid_active_filter: '状态筛选不合法',
    invalid_platform: '平台只能是 Web / Client',
    invalid_placement_code: '广告位代码不合法',
    invalid_row_no: '行号不合法',
    invalid_column_no: '列号不合法',
    title_required: '标题不能为空',
    image_url_required: '图片 URL 不能为空',
    click_url_required: '跳转 URL 不能为空',
    invalid_sort_order: '排序值不合法',
    invalid_time_range: '开始时间必须早于过期时间',
    invalid_click_url: '跳转 URL 必须使用 HTTPS 完整地址',
    coordinate_conflict: '该广告位坐标已存在，请编辑原广告或换一个行/列',
    not_found: '广告位不存在（可能已被删除）',
  }
  return msgMap[message] || message || '操作失败'
}

/* ── page ────────────────────────────────────────────────────── */

function AdSlotsPage() {
  const qc = useQueryClient()
  const { message } = AntdApp.useApp()

  const [placement, setPlacement] = useState<PlacementCode | 'all'>('all')
  const [platform, setPlatform] = useState<PlatformFilter>('all')
  const [active, setActive] = useState<ActiveFilter>('all')
  const [editing, setEditing] = useState<AdSlotRow | 'new' | null>(null)
  const isMobile = useIsMobile()

  const listQuery = useQuery({
    queryKey: ['ad-slots', placement, platform, active],
    placeholderData: keepPreviousData,
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .schema('admin')
        .rpc('list_ad_slots', {
          p_placement_code: placement === 'all' ? null : placement,
          p_platform: platform === 'all' ? null : platform,
          p_active: active,
          p_limit: 200,
          p_offset: 0,
        })
      if (error) throw error
      const r = data as ListResult
      if (!r.ok) throw new Error(r.error ?? 'unknown')
      return r.rows ?? []
    },
  })

  const toggleActiveMutation = useMutation({
    mutationFn: async (params: { id: string; isActive: boolean }) => {
      const { data, error } = await (supabase as any)
        .schema('admin')
        .rpc('set_ad_slot_active', {
          p_id: params.id,
          p_is_active: params.isActive,
          p_reason: null,
        })
      if (error) throw error
      const r = data as { ok: boolean; error?: string }
      if (!r.ok) throw new Error(r.error ?? 'unknown')
      return r
    },
    onSuccess: (_, vars) => {
      message.success(vars.isActive ? '已启用' : '已停用')
      qc.invalidateQueries({ queryKey: ['ad-slots'] })
      void revalidateNextTag('ads')
    },
    onError: (e: Error) => message.error(mapErrorMessage(e.message)),
  })

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { data, error } = await (supabase as any)
        .schema('admin')
        .rpc('delete_ad_slot', { p_id: id, p_reason: null })
      if (error) throw error
      const r = data as { ok: boolean; error?: string }
      if (!r.ok) throw new Error(r.error ?? 'unknown')
      return r
    },
    onSuccess: () => {
      message.success('已删除')
      qc.invalidateQueries({ queryKey: ['ad-slots'] })
      void revalidateNextTag('ads')
    },
    onError: (e: Error) => message.error(mapErrorMessage(e.message)),
  })

  const columns: ColumnsType<AdSlotRow> = useMemo(
    () => [
      {
        title: '广告素材',
        width: 300,
        render: (_: unknown, row) => {
          const textLink = isTextLinkPlacement(row.placement_code)
          return (
            <Space size={10} align="start">
              {textLink ? (
                <Tag color="magenta" style={{ marginTop: 2 }}>
                  文字链接
                </Tag>
              ) : (
                <Image
                  src={row.image_url}
                  alt={row.alt_text ?? row.title}
                  width={84}
                  height={48}
                  preview={{ mask: <EyeOutlined /> }}
                  style={{ objectFit: 'cover', borderRadius: 4 }}
                />
              )}
              <Space direction="vertical" size={2} style={{ minWidth: 0 }}>
                <Typography.Text strong style={{ fontSize: 13 }} ellipsis>
                  {row.title}
                </Typography.Text>
                <Typography.Text
                  type="secondary"
                  style={{ fontSize: 11 }}
                  ellipsis
                >
                  {textLink
                    ? 'C 端显示标题文字并跳转'
                    : row.alt_text || '未填写 alt 文案'}
                </Typography.Text>
                <Typography.Text
                  type="secondary"
                  style={{ fontSize: 11 }}
                  ellipsis
                >
                  {textLink ? row.click_url : row.image_url}
                </Typography.Text>
              </Space>
            </Space>
          )
        },
      },
      {
        title: '位置',
        width: 210,
        render: (_: unknown, row) => {
          const meta = placementMeta(row.placement_code)
          return (
            <Space direction="vertical" size={2}>
              <Tag color={meta.color} style={{ marginRight: 0 }}>
                {meta.label}
              </Tag>
              <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                {slotCoordinate(row)}
              </Typography.Text>
              <Typography.Text
                type="secondary"
                style={{ fontSize: 11 }}
                ellipsis
              >
                {row.placement_code}
              </Typography.Text>
            </Space>
          )
        },
      },
      {
        title: '平台',
        dataIndex: 'platform',
        width: 120,
        render: (value: Platform) => (
          <Tag color={value === 'web' ? 'blue' : 'green'}>
            {value === 'web' ? 'Web' : 'Client'}
          </Tag>
        ),
      },
      {
        title: '跳转',
        width: 210,
        render: (_: unknown, row) => (
          <Tooltip title={row.click_url}>
            <Typography.Link
              href={row.click_url}
              target="_blank"
              rel="noreferrer"
              onClick={(e) => e.stopPropagation()}
              ellipsis
              style={{ maxWidth: 180, display: 'inline-block' }}
            >
              <LinkOutlined /> {row.click_url}
            </Typography.Link>
          </Tooltip>
        ),
      },
      {
        title: '投放时段',
        width: 190,
        render: (_: unknown, row) => (
          <Space direction="vertical" size={0}>
            <Typography.Text style={{ fontSize: 11 }}>
              {row.starts_at ? formatCST(row.starts_at, 'MM-DD HH:mm') : '立即'}
            </Typography.Text>
            <Typography.Text type="secondary" style={{ fontSize: 11 }}>
              →{' '}
              {row.expires_at
                ? formatCST(row.expires_at, 'MM-DD HH:mm')
                : '永不过期'}
            </Typography.Text>
          </Space>
        ),
      },
      {
        title: '排序',
        dataIndex: 'sort_order',
        width: 70,
        render: (n: number) => (
          <Typography.Text
            type="secondary"
            style={{ fontSize: 12, fontVariantNumeric: 'tabular-nums' }}
          >
            {n}
          </Typography.Text>
        ),
      },
      {
        title: '状态',
        width: 95,
        render: (_: unknown, row) => {
          const state = getDeliveryState(row)
          return (
            <Tooltip title={state.hint}>
              <Tag color={state.color}>{state.label}</Tag>
            </Tooltip>
          )
        },
      },
      {
        title: '更新于',
        dataIndex: 'updated_at',
        width: 130,
        render: (t: string) => (
          <Typography.Text type="secondary" style={{ fontSize: 11 }}>
            {formatCST(t, 'MM-DD HH:mm')}
          </Typography.Text>
        ),
      },
      {
        title: '操作',
        width: 210,
        fixed: 'right',
        render: (_: unknown, row) => (
          <Space size={4}>
            <Button
              type="link"
              size="small"
              icon={<EditOutlined />}
              onClick={(e) => {
                e.stopPropagation()
                setEditing(row)
              }}
            >
              编辑
            </Button>
            <Popconfirm
              title={row.is_active ? '停用该广告位？' : '启用该广告位？'}
              description={
                row.is_active
                  ? 'C 端将不再读取到该广告。'
                  : '若时间窗口有效，C 端会读取到该广告。'
              }
              onConfirm={() =>
                toggleActiveMutation.mutate({
                  id: row.id,
                  isActive: !row.is_active,
                })
              }
              okText={row.is_active ? '停用' : '启用'}
              cancelText="取消"
              okButtonProps={{ danger: row.is_active }}
            >
              <Button
                type="link"
                size="small"
                danger={row.is_active}
                onClick={(e) => e.stopPropagation()}
              >
                {row.is_active ? '停用' : '启用'}
              </Button>
            </Popconfirm>
            <Popconfirm
              title="删除该广告位？"
              description="硬删除，无法恢复。写动作仍会留在审计日志里。"
              onConfirm={() => deleteMutation.mutate(row.id)}
              okText="删除"
              cancelText="取消"
              okButtonProps={{ danger: true }}
            >
              <Button
                type="link"
                size="small"
                danger
                icon={<DeleteOutlined />}
                onClick={(e) => e.stopPropagation()}
              />
            </Popconfirm>
          </Space>
        ),
      },
    ],
    [deleteMutation, toggleActiveMutation],
  )

  const rows = listQuery.data ?? []
  const filteredPlacementOptions =
    platform === 'all'
      ? PLACEMENT_OPTIONS
      : PLACEMENT_OPTIONS.filter((option) =>
          supportsPlatform(platform, option.value),
        )
  const defaultDrawerPlatform =
    platform === 'all' ? defaultPlatformForPlacement(placement) : platform
  const defaultDrawerPlacement =
    placement === 'all'
      ? defaultPlacementForPlatform(defaultDrawerPlatform)
      : placement

  return (
    <>
      <Card
        title="广告位"
        extra={
          <Space>
            <Tooltip title={isMobile ? '刷新' : undefined}>
              <Button
                icon={<ReloadOutlined />}
                aria-label="刷新"
                onClick={() => listQuery.refetch()}
                loading={listQuery.isFetching && !listQuery.isLoading}
              >
                {isMobile ? null : '刷新'}
              </Button>
            </Tooltip>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => setEditing('new')}
            >
              新建广告
            </Button>
          </Space>
        }
      >
        <AdminFilterBar
          className="ad-slots-filter"
          activeCount={
            (platform !== 'all' ? 1 : 0) + (active !== 'all' ? 1 : 0)
          }
          primary={
            <Select<PlacementCode | 'all'>
              value={placement}
              onChange={setPlacement}
              style={{ width: 220 }}
              options={[
                { value: 'all', label: '全部广告位' },
                ...filteredPlacementOptions,
              ]}
            />
          }
        >
          <Select<PlatformFilter>
            value={platform}
            onChange={(nextPlatform) => {
              setPlatform(nextPlatform)
              if (
                nextPlatform !== 'all' &&
                placement !== 'all' &&
                !supportsPlatform(nextPlatform, placement)
              ) {
                setPlacement('all')
              }
            }}
            style={{ width: 180 }}
            options={[{ value: 'all', label: '全部平台' }, ...PLATFORM_OPTIONS]}
          />
          <Segmented<ActiveFilter>
            value={active}
            onChange={(v) => setActive(v)}
            options={[
              { label: '全部', value: 'all' },
              { label: '启用', value: 'active' },
              { label: '停用', value: 'inactive' },
            ]}
          />
        </AdminFilterBar>

        <div className="admin-table-wrap">
          <Table<AdSlotRow>
            rowKey="id"
            size="middle"
            loading={listQuery.isLoading}
            columns={columns}
            dataSource={rows}
            onRow={(row) => ({
              onClick: () => setEditing(row),
              style: { cursor: 'pointer' },
            })}
            scroll={{ x: 1420 }}
            pagination={{
              pageSize: 50,
              showSizeChanger: false,
              showTotal: (total) => `共 ${total} 条`,
            }}
          />
        </div>

        <AdminMobileList
          rows={rows}
          rowKey={(row) => row.id}
          loading={listQuery.isLoading || listQuery.isFetching}
          renderItem={(row) => {
            const state = getDeliveryState(row)
            const meta = placementMeta(row.placement_code)
            return (
              <article
                className="admin-mobile-record"
                role="button"
                tabIndex={0}
                aria-label={`编辑 ${row.title}`}
                onClick={() => setEditing(row)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') setEditing(row)
                }}
              >
                <div className="admin-mobile-record__head">
                  <div className="admin-mobile-record__title">{row.title}</div>
                  <Tag color={state.color}>{state.label}</Tag>
                </div>
                <div className="admin-mobile-record__lead">
                  {isTextLinkPlacement(row.placement_code) ? (
                    <Tag color="magenta" style={{ marginTop: 2 }}>
                      文字链接
                    </Tag>
                  ) : (
                    <Image
                      src={row.image_url}
                      alt={row.alt_text ?? row.title}
                      width={72}
                      height={44}
                      preview={false}
                      style={{ objectFit: 'cover', borderRadius: 4 }}
                    />
                  )}
                  <div className="admin-mobile-record__meta">
                    <Tag color={meta.color}>{meta.label}</Tag>
                    <Tag color={row.platform === 'web' ? 'blue' : 'green'}>
                      {row.platform === 'web' ? 'Web' : 'Client'}
                    </Tag>
                    <span>
                      {row.row_no} 行 / {row.column_no} 列 · 排序{' '}
                      {row.sort_order}
                    </span>
                  </div>
                </div>
                <div className="admin-mobile-record__body ad-slots-mobile-url">
                  <LinkOutlined /> {row.click_url}
                </div>
                <div className="admin-mobile-record__meta">
                  <span>
                    {row.starts_at
                      ? formatCST(row.starts_at, 'MM-DD HH:mm')
                      : '立即'}
                    {' → '}
                    {row.expires_at
                      ? formatCST(row.expires_at, 'MM-DD HH:mm')
                      : '永不过期'}
                  </span>
                  <span>更新 {formatCST(row.updated_at, 'MM-DD HH:mm')}</span>
                </div>
                <AdminRecordActions
                  actions={[
                    {
                      key: 'toggle',
                      label: row.is_active ? '停用' : '启用',
                      danger: row.is_active,
                      primary: true,
                      onClick: () =>
                        toggleActiveMutation.mutate({
                          id: row.id,
                          isActive: !row.is_active,
                        }),
                      confirm: {
                        title: row.is_active
                          ? '停用该广告位？'
                          : '启用该广告位？',
                        description: row.is_active
                          ? 'C 端将不再读取到该广告。'
                          : '若时间窗口有效，C 端会读取到该广告。',
                        okText: row.is_active ? '停用' : '启用',
                      },
                    },
                    {
                      key: 'delete',
                      label: '删除',
                      icon: <DeleteOutlined />,
                      danger: true,
                      primary: true,
                      onClick: () => deleteMutation.mutate(row.id),
                      confirm: {
                        title: '删除该广告位？',
                        description:
                          '硬删除，无法恢复。写动作仍会留在审计日志里。',
                        okText: '删除',
                      },
                    },
                  ]}
                />
              </article>
            )
          }}
        />
      </Card>

      <EditDrawer
        open={editing !== null}
        row={editing === 'new' ? null : editing}
        defaultPlacement={defaultDrawerPlacement}
        defaultPlatform={defaultDrawerPlatform}
        onClose={() => setEditing(null)}
        onSaved={() => {
          setEditing(null)
          qc.invalidateQueries({ queryKey: ['ad-slots'] })
        }}
      />
    </>
  )
}

/* ── edit drawer ─────────────────────────────────────────────── */

function EditDrawer({
  open,
  row,
  defaultPlacement,
  defaultPlatform,
  onClose,
  onSaved,
}: {
  open: boolean
  row: AdSlotRow | null
  defaultPlacement: PlacementCode
  defaultPlatform: Platform
  onClose: () => void
  onSaved: () => void
}) {
  const { message } = AntdApp.useApp()
  const isMobile = useIsMobile()
  const [form] = Form.useForm<FormValues>()
  const isNew = !row
  const watchedPlacement = Form.useWatch('placement_code', form) as
    | PlacementCode
    | undefined
  const watchedPlatform = Form.useWatch('platform', form) as
    | Platform
    | undefined
  const placementCode = watchedPlacement ?? defaultPlacement
  const platformCode = watchedPlatform ?? defaultPlatform
  const imageUrl = Form.useWatch('image_url', form)
  const currentMeta = placementMeta(placementCode)
  const currentLayout = placementLayout(platformCode, placementCode)
  const textLinkPlacement = isTextLinkPlacement(placementCode)
  const drawerPlacementOptions = useMemo(
    () =>
      PLACEMENT_OPTIONS.filter((option) =>
        supportsPlatform(platformCode, option.value),
      ),
    [platformCode],
  )

  useEffect(() => {
    if (!open) return
    if (row) {
      form.setFieldsValue({
        platform: row.platform,
        placement_code: row.placement_code as PlacementCode,
        row_no: row.row_no,
        column_no: row.column_no,
        title: row.title,
        image_url: row.image_url,
        click_url: row.click_url,
        alt_text: row.alt_text ?? '',
        is_active: row.is_active,
        time_range:
          row.starts_at || row.expires_at
            ? [
                row.starts_at ? toCSTDayjs(row.starts_at) : null,
                row.expires_at ? toCSTDayjs(row.expires_at) : null,
              ]
            : null,
        sort_order: row.sort_order,
        reason: '',
      })
    } else {
      form.resetFields()
      form.setFieldsValue({
        platform: defaultPlatform,
        placement_code: defaultPlacement,
        row_no: 1,
        column_no: 1,
        is_active: true,
        sort_order: 0,
      })
    }
  }, [defaultPlacement, defaultPlatform, form, open, row])

  useEffect(() => {
    if (!open || !watchedPlacement || !watchedPlatform) return
    const rowNo = form.getFieldValue('row_no')
    const columnNo = form.getFieldValue('column_no')
    if (!rowNo || rowNo > currentLayout.rows) form.setFieldValue('row_no', 1)
    if (!columnNo || columnNo > currentLayout.columns) {
      form.setFieldValue('column_no', 1)
    }
  }, [
    currentLayout.columns,
    currentLayout.rows,
    form,
    open,
    watchedPlacement,
    watchedPlatform,
  ])

  useEffect(() => {
    if (
      !open ||
      !watchedPlacement ||
      !watchedPlatform ||
      supportsPlatform(watchedPlatform, watchedPlacement)
    ) {
      return
    }
    form.setFieldValue(
      'placement_code',
      watchedPlacement === 'player_pause' ||
        watchedPlacement === 'player_pause_overlay'
        ? watchedPlatform === 'client'
          ? 'player_pause'
          : 'player_pause_overlay'
        : defaultPlacementForPlatform(watchedPlatform),
    )
  }, [form, open, watchedPlacement, watchedPlatform])

  const saveMutation = useMutation({
    mutationFn: async (values: FormValues) => {
      if (!supportsPlatform(values.platform, values.placement_code)) {
        throw new Error('placement_not_supported_on_platform')
      }
      const layout = placementLayout(values.platform, values.placement_code)
      if (values.row_no > layout.rows || values.column_no > layout.columns) {
        throw new Error('coordinate_out_of_catalog')
      }
      const { data, error } = await (supabase as any)
        .schema('admin')
        .rpc('upsert_ad_slot', {
          p_id: row?.id ?? null,
          p_platform: values.platform,
          p_placement_code: values.placement_code,
          p_row_no: values.row_no,
          p_column_no: values.column_no,
          p_title: values.title.trim(),
          p_image_url:
            (values.image_url ?? '').trim() ||
            (isTextLinkPlacement(values.placement_code)
              ? `text-link://${values.placement_code}`
              : ''),
          p_click_url: values.click_url.trim(),
          p_alt_text: values.alt_text?.trim() || null,
          p_is_active: values.is_active,
          p_starts_at: pickerToUTCISO(values.time_range?.[0] ?? null),
          p_expires_at: pickerToUTCISO(values.time_range?.[1] ?? null),
          p_sort_order: values.sort_order,
          p_reason: values.reason.trim() || null,
        })
      if (error) throw error
      const r = data as { ok: boolean; error?: string; id?: string }
      if (!r.ok) throw new Error(r.error ?? 'unknown')
      return r
    },
    onSuccess: () => {
      message.success(isNew ? '已创建' : '已保存')
      onSaved()
      void revalidateNextTag('ads')
    },
    onError: (e: Error) => {
      if (e.message === 'coordinate_out_of_catalog') {
        message.error('行/列超出该广告位的前端布局范围')
        return
      }
      if (e.message === 'placement_not_supported_on_platform') {
        message.error('该广告位不支持当前平台')
        return
      }
      message.error(mapErrorMessage(e.message))
    },
  })

  return (
    <Drawer
      open={open}
      onClose={onClose}
      width={isMobile ? '100%' : 680}
      title={isNew ? '新建广告位' : `编辑 · ${row.title}`}
      destroyOnHidden
      footer={
        <Space style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button onClick={onClose}>取消</Button>
          <Button
            type="primary"
            loading={saveMutation.isPending}
            onClick={() => form.submit()}
          >
            {isNew ? '创建' : '保存'}
          </Button>
        </Space>
      }
    >
      <Form<FormValues>
        form={form}
        layout="vertical"
        requiredMark
        onFinish={(v) => saveMutation.mutate(v)}
      >
        <Form.Item
          name="platform"
          label="平台"
          rules={[{ required: true, message: '请选择平台' }]}
        >
          <Select options={PLATFORM_OPTIONS} />
        </Form.Item>

        <Form.Item
          label="广告位"
          name="placement_code"
          rules={[{ required: true, message: '请选择广告位' }]}
          tooltip={currentMeta.location}
        >
          <Select options={drawerPlacementOptions} />
        </Form.Item>

        <Typography.Text
          type="secondary"
          style={{ display: 'block', marginTop: -12, marginBottom: 12 }}
        >
          当前布局：{platformCode === 'web' ? 'Web' : 'Client'} ·{' '}
          {currentLayout.rows} × {currentLayout.columns}
        </Typography.Text>

        <Space style={{ display: 'flex', marginBottom: 0 }} align="start">
          <Form.Item
            label="行"
            name="row_no"
            rules={[{ required: true, message: '请输入行号' }]}
            tooltip={`${placementLabel(placementCode)} 支持 1-${currentLayout.rows} 行`}
            style={{ flex: 1 }}
          >
            <InputNumber
              min={1}
              max={currentLayout.rows}
              style={{ width: '100%' }}
            />
          </Form.Item>
          <Form.Item
            label="列"
            name="column_no"
            rules={[{ required: true, message: '请输入列号' }]}
            tooltip={`${placementLabel(placementCode)} 支持 1-${currentLayout.columns} 列`}
            style={{ flex: 1 }}
          >
            <InputNumber
              min={1}
              max={currentLayout.columns}
              style={{ width: '100%' }}
            />
          </Form.Item>
          <Form.Item
            label="排序"
            name="sort_order"
            tooltip="数字越小越靠前。首页轮播位直接以此字段决定播放顺序（同值时按行列兜底）；其余素材位在同行列内生效"
            style={{ flex: 1 }}
          >
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
        </Space>

        <Form.Item
          label="标题"
          name="title"
          rules={[
            { required: true, message: '请输入标题' },
            { max: 120, message: '最长 120 字' },
          ]}
        >
          <Input placeholder="后台识别用，也可作为图片 title" />
        </Form.Item>

        <Form.Item
          label={textLinkPlacement ? '兼容图片 URL' : '图片 URL'}
          name="image_url"
          rules={
            textLinkPlacement
              ? []
              : [{ required: true, message: '请输入图片 URL' }]
          }
          tooltip={
            textLinkPlacement
              ? '文字链接广告不展示图片；留空会写入内部占位值以兼容旧表结构'
              : undefined
          }
        >
          <Input
            placeholder={
              textLinkPlacement
                ? '可留空，C 端不会读取这个字段'
                : 'https://image.example.com/ad.webp'
            }
          />
        </Form.Item>
        {imageUrl && !textLinkPlacement ? (
          <div style={{ marginTop: -8, marginBottom: 16 }}>
            <Image
              src={imageUrl}
              alt="广告素材预览"
              width={220}
              height={124}
              style={{ objectFit: 'cover', borderRadius: 4 }}
            />
          </div>
        ) : null}

        <Form.Item
          label="跳转 URL"
          name="click_url"
          rules={[
            { required: true, message: '请输入跳转 URL' },
            {
              validator: (_, value: string | undefined) => {
                if (!value || /^https:\/\/\S+$/i.test(value.trim())) {
                  return Promise.resolve()
                }
                return Promise.reject(
                  new Error('必须使用 HTTPS 完整地址，不能填写内部路径'),
                )
              },
            },
          ]}
        >
          <Input placeholder="https://example.com/path" />
        </Form.Item>

        <Form.Item label="Alt 文案（可选）" name="alt_text">
          <Input
            placeholder="图片替代文本；不填时 C 端可用标题兜底"
            maxLength={200}
          />
        </Form.Item>

        <Form.Item
          label="投放时段（可选；不填即立即生效 / 永不过期）"
          name="time_range"
        >
          <DatePicker.RangePicker
            showTime={{ format: 'HH:mm' }}
            format="YYYY-MM-DD HH:mm"
            style={{ width: '100%' }}
            placeholder={['开始时间', '过期时间']}
            allowEmpty={[true, true]}
          />
        </Form.Item>

        <Space size="large" wrap style={{ marginBottom: 16 }}>
          <Form.Item
            label="启用"
            name="is_active"
            valuePropName="checked"
            style={{ marginBottom: 0 }}
            tooltip="关闭后即使时间窗口有效，C 端也不会读取到"
          >
            <Switch />
          </Form.Item>
        </Space>

        <Form.Item
          label="备注（会写入审计日志）"
          name="reason"
          rules={[{ required: true, message: '请输入备注' }]}
        >
          <Input.TextArea
            autoSize={{ minRows: 2, maxRows: 3 }}
            maxLength={500}
            showCount
            placeholder="说明本次操作原因，写入审计日志"
          />
        </Form.Item>
      </Form>
    </Drawer>
  )
}
